import { every } from 'lodash';
import {getAccountsForPortfolioGrowth, getPortfolioGrowthFilter} from './PortfolioGrowthFilterSelectors';
import {getAccountsForActivitySummary, getActivitySummaryFilter} from './ActivitySummaryFilterSelectors';
import { getRateOfReturnFilter } from './RateOfReturnFilterSelectors';
import { filterProductTypeOptions } from './RateOfReturnFilterReducer';
import { minInceptionDate } from 'services/Calculation';
import { createSelector } from 'reselect';
import {
  getAccountForDetails,
  getAccounts,
  getPriorBusinessDay,
  isBrokerageInceptionDateBefore2019
} from 'state/account/AccountSelectors';
import {
  isActivePlus,
  isAlaska529,
  isBrokerage,
  isCollegeSavings
} from 'services/Products';
import {
  applyPerformanceFilters,
  createTimeRangeFromFilter,
  checkIsAggregateRateOfReturnEligible
} from './PerformancePageUtils';
import { yyyymmdd } from 'services/Dates';
import {
  getHasReceivedAggregatedProductPerformance,
  getHasReceivedRateOfReturn
} from '../requests/RequestStatusSelectors';
import Time from 'services/Time';
import {
  getPerformanceSelectedPeriod,
  onAccountDetailsPage
} from 'state/view/ViewSelectors';
import {
  ALL_LABEL,
  APP_LABEL,
  BROKERAGE_LABEL,
  timePeriodLabelMap
} from 'services/Filters';
import { getPortfolioAsOfDate } from 'state/portfolio/PortfolioSelectors';

export const ACTIVITY_SUMMARY_FILTER_TYPE = 'activitySummary';
export const PORTFOLIO_GROWTH_FILTER_TYPE = 'portfolioGrowth';
export const PRR_FILTER_TYPE = 'rateOfReturn';

export const getPerformancePage = (state) => state.performancePage;

export const getAccountIdsByTypeMap = createSelector(
  getPerformancePage,
  (performancePage) => performancePage?.accountIdsByType || {}
);

const getPerformanceAccounts = createSelector(
  getAccounts,
  getAccountIdsByTypeMap,
  (accounts, mapping) =>
    accounts.filter((a) => mapping[ALL_LABEL].includes(a.accountId))
);
export const has529Only = createSelector(getPerformanceAccounts, (accounts) =>
  every(accounts, (a) => isCollegeSavings(a.productType))
);

// Page Level checks
export const hasAlaska529Only = createSelector(
  getPerformanceAccounts,
  (accounts) => every(accounts, (a) => isAlaska529(a.productType))
);

export const hasBrokerage = createSelector(
  getAccountIdsByTypeMap,
  (accountIdsByType) => !!accountIdsByType[BROKERAGE_LABEL]?.length
);

export const hasBrokeragePerformanceOnly = createSelector(
  getPerformanceAccounts,
  (accounts) => every(accounts, (a) => isBrokerage(a.productType))
);

export const hasActivePlus = createSelector(
  getAccountIdsByTypeMap,
  (accountIdsByType) => !!accountIdsByType[APP_LABEL]?.length
);

export const isAggregateRateOfReturnEligible = createSelector(
  getAccountIdsByTypeMap,
  (accountIdsByType) => checkIsAggregateRateOfReturnEligible(accountIdsByType)
);

export const shouldUseAggregatedPerformance = createSelector(
  [
    isAggregateRateOfReturnEligible,
    (state) => getAccountTypeSelectionForFilterType(state, PRR_FILTER_TYPE)
  ],
  (aggregateRateOfReturnEligible, selected) =>
    aggregateRateOfReturnEligible && selected === ALL_LABEL
);

export const getFilterForFilterType = (state, filterType) =>
  filterType === PORTFOLIO_GROWTH_FILTER_TYPE
    ? getPortfolioGrowthFilter(state)
    : filterType === ACTIVITY_SUMMARY_FILTER_TYPE
    ? getActivitySummaryFilter(state)
    : filterType === PRR_FILTER_TYPE
    ? getRateOfReturnFilter(state)
    : null;

export const getAccountTypeSelectionForFilterType = createSelector(
  getFilterForFilterType,
  (performanceFilter) => performanceFilter.filter?.label || ''
);

export const getAccountsForFilterType = createSelector(
  [
    // eslint-disable-next-line no-unused-vars
    (state, _) => getAccounts(state),
    // eslint-disable-next-line no-unused-vars
    (state, _) => getPerformancePage(state),
    getFilterForFilterType
  ],
  (allAccounts, performancePage, performanceFilter) =>
    applyPerformanceFilters(performancePage, performanceFilter, allAccounts)
);

export const getLabelForFilterType = (filterType) => {
  switch (filterType) {
    case PRR_FILTER_TYPE:
      return 'Filter by Investments';
    default:
      return 'Filter by Account Type';
  }
};

const isPershingDependent = createSelector(
  hasBrokerage,
  hasActivePlus,
  (brokerage, app) => brokerage || app
);

export const showWarning = createSelector(
  isPershingDependent,
  getFilterForFilterType,
  (pershingDependent, performanceFilter) => {
    const { filter, dateRangeFilter } = performanceFilter;
    const isSinceInception = dateRangeFilter.period === 'sinceInception';
    const appOrBrokerage =
      filter.label === APP_LABEL || filter.label === BROKERAGE_LABEL;
    const allSelected = filter.label === ALL_LABEL;

    return (
      isSinceInception && pershingDependent && (appOrBrokerage || allSelected)
    );
  }
);

export const showPerformancePagePortfolioGrowthBrokerageWarning = createSelector(
  isPershingDependent,
  getAccountsForPortfolioGrowth,
  isBrokerageInceptionDateBefore2019,
  (pershingDependent, accountsForPortfolioGrowth,  brokerageInceptionDate) => {

    const containsBrokerage = accountsForPortfolioGrowth.some((account) => isBrokerage(account.productType));
    return (
      brokerageInceptionDate && pershingDependent && containsBrokerage
    );
  }
);

export const showPerformancePageActivitySummaryBrokerageWarning = createSelector(
  isPershingDependent,
  getAccountsForActivitySummary,
  isBrokerageInceptionDateBefore2019,
  (pershingDependent, accountsForActivitySummary, brokerageInceptionDate) => {

    const containsBrokerage = accountsForActivitySummary.some((account) => isBrokerage(account.productType));
    return (
      brokerageInceptionDate && pershingDependent && containsBrokerage
    );
  }
);

export const getMinInceptionDateFromFilter = (accountIds) => {
  const accounts = Object.values(accountIds);
  return minInceptionDate(accounts);
};

export const getPerformancePageOptions = createSelector(
  getAccountIdsByTypeMap,
  (accountIdsByType) =>
    Object.keys(accountIdsByType).filter(
      (type) => !!accountIdsByType[type].length
    )
);

export const getRateOfReturnOptions = createSelector(
  getPerformancePageOptions,
  isAggregateRateOfReturnEligible,
  (options, aggregateRateOfReturnEligible) => {
    const filteredOptions = filterProductTypeOptions(options);

    if (aggregateRateOfReturnEligible) {
      filteredOptions.unshift(ALL_LABEL);
    }

    return filteredOptions;
  }
);

export const getSelectedProductTypeForFilterType = createSelector(
  [
    getAccountsForFilterType,
    getAccountTypeSelectionForFilterType,
    // eslint-disable-next-line no-unused-vars
    (state, _) => hasActivePlus(state),
    // eslint-disable-next-line no-unused-vars
    (state, _) => hasBrokerage(state)
  ],
  (filteredAccounts, selected, hasActivePlus, hasBrokerage) => {
    if (
      (selected === ALL_LABEL && hasActivePlus && hasBrokerage) ||
      (filteredAccounts.some(({ productType }) => isActivePlus(productType)) &&
        filteredAccounts.some(({ productType }) => isBrokerage(productType)))
    ) {
      return 'activePlusBrokerage';
    }
    if (
      selected === APP_LABEL ||
      filteredAccounts.some(({ productType }) => isActivePlus(productType))
    ) {
      return 'activePlus';
    }
    if (
      selected === BROKERAGE_LABEL ||
      filteredAccounts.some(({ productType }) => isBrokerage(productType))
    ) {
      return 'brokerage';
    }
  }
);

export const getPeriodSelection = createSelector(
  getFilterForFilterType,
  (performanceFilter) => {
    const { dateRangeFilter } = performanceFilter;
    const key = dateRangeFilter.period;
    const selectedPeriod = timePeriodLabelMap[key];

    return {
      title: selectedPeriod,
      value: key
    };
  }
);

export const getPerformanceOptions = createSelector(
  getPerformanceSelectedPeriod,
  // eslint-disable-next-line no-unused-vars
  (selectedPeriod) => {
    let periodKeys = Object.keys(timePeriodLabelMap);
    const canViewYTDPeriod = Time.now > Time.getFirstBusinessDayOfTheYear();

    const options = periodKeys.map((key) => {
      const periodLabel = timePeriodLabelMap[key];
      return {
        title: periodLabel,
        value: key
      };
    });

    if (!canViewYTDPeriod) {
      let removeYTDFromOptions = options
        .map(function (option) {
          return option.value;
        })
        .indexOf('yearToDate');
      options.splice(removeYTDFromOptions, 1);
    }

    return options;
  }
);

export const getInceptionDateForFilterType = createSelector(
  [
    // eslint-disable-next-line no-unused-vars
    (state, _) => onAccountDetailsPage(state),
    getAccountsForFilterType,
    // eslint-disable-next-line no-unused-vars
    (state, _) => getAccountForDetails(state),
    // eslint-disable-next-line no-unused-vars
    (state, _) => getPriorBusinessDay(state)
  ],
  (onAccountDetails, accounts, accountForDetails, priorBusinessDay) =>
    onAccountDetails
      ? accountForDetails.inceptionDate
      : minInceptionDate(accounts) || priorBusinessDay
);

export const getDateRangeFilterForFilterType = createSelector(
  getFilterForFilterType,
  (performanceFilter) => performanceFilter.dateRangeFilter
);

export const isCustomDateForFilterType = createSelector(
  getFilterForFilterType,
  (performanceFilter) => performanceFilter.dateRangeFilter?.custom || false
);

export const getDateRangeForFilterType = createSelector(
  getInceptionDateForFilterType,
  getDateRangeFilterForFilterType,
  getPortfolioAsOfDate,
  (inceptionDate, dateRangeFilter, portfolioAsOfDate) =>
    createTimeRangeFromFilter(inceptionDate, dateRangeFilter, portfolioAsOfDate)
);

export const getFormattedDateRangeForFilterType = createSelector(
  getDateRangeForFilterType,
  (dateRange) => ({
    fromDate: yyyymmdd(dateRange.fromDate),
    toDate: yyyymmdd(dateRange.toDate)
  })
);

export const getPerformanceFilterOptions = createSelector(
  [
    getPerformancePageOptions,
    getRateOfReturnOptions,
    (state, filterType) => filterType
  ],
  (allOptions, prrOptions, filterType) => {
    const optionList = filterType === PRR_FILTER_TYPE ? prrOptions : allOptions;
    return optionList.map((option) => ({ title: option, value: option }));
  }
);

export const hasLoadedRateOfReturn = createSelector(
  [
    // eslint-disable-next-line no-unused-vars
    (state, _) => getAccountTypeSelectionForFilterType(state, PRR_FILTER_TYPE),
    getHasReceivedAggregatedProductPerformance,
    getHasReceivedRateOfReturn,
    isAggregateRateOfReturnEligible
  ],
  (selected, receivedAgg, receivedPrr, aggregateRateOfReturnEligible) =>
    aggregateRateOfReturnEligible && selected === ALL_LABEL
      ? receivedAgg
      : receivedPrr
);
